import { useState } from "react";
import { downloadCsv } from "../utils/csvExport";

export type ExportPhase = "fetching" | "translating" | "generating" | "done";

export type ExportProgress = {
  phase: ExportPhase;
  current: number;
  total: number;
  percentage: number;
};

type UseExportOptions<T, R = T> = {
  batchSize?: number;
  fetchData: (params: { limit: number; offset: number; skipCount?: boolean }) => Promise<{
    data: T[];
    pagination?: { total: number };
  }>;
  processData?: (data: T[], onProgress?: (current: number, total: number) => void) => Promise<R[]>;
  generateCsv: (data: R[]) => string;
  getFilename: () => string;
  emptyMessage?: string;
  errorMessage?: string;
};

export function useExport<T, R = T>({
  batchSize = 1000,
  fetchData,
  processData,
  generateCsv,
  getFilename,
  emptyMessage = "No data found to export",
  errorMessage = "Failed to export data",
}: UseExportOptions<T, R>) {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ExportProgress | null>(null);

  const exportData = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setError(null);
    setProgress(null);

    let offset = 0;
    const allRows: T[] = [];

    try {
      // First request with limit=1 to get total count
      const probe = await fetchData({ limit: 1, offset: 0 });
      const total = probe.pagination?.total ?? 0;
      setProgress({ phase: "fetching", current: 0, total, percentage: 0 });

      if (total === 0) {
        setError(emptyMessage);
        return;
      }

      // Add the probed row
      if (probe.data?.length) {
        allRows.push(...probe.data);
        offset = 1;
      }

      while (true) {
        const resp = await fetchData({ limit: batchSize, offset, skipCount: true });
        const rows = resp.data ?? [];

        allRows.push(...rows);
        offset += batchSize;

        const current = allRows.length;
        const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
        setProgress({ phase: "fetching", current, total, percentage });

        if (rows.length < batchSize) {
          break;
        }
      }

      if (allRows.length === 0) {
        setError(emptyMessage);
        return;
      }

      let processedRows: R[];

      if (processData) {
        setProgress({ phase: "translating", current: 0, total: 0, percentage: 0 });

        processedRows = await processData(allRows, (current, languageTotal) => {
          const percentage = languageTotal > 0 ? Math.round((current / languageTotal) * 100) : 0;
          setProgress({
            phase: "translating",
            current,
            total: languageTotal,
            percentage,
          });
        });
      } else {
        processedRows = allRows as unknown as R[];
      }

      setProgress({ phase: "generating", current: 0, total: 0, percentage: 100 });
      const csv = generateCsv(processedRows);
      downloadCsv(csv, getFilename());

      setProgress({
        phase: "done",
        current: processedRows.length,
        total: processedRows.length,
        percentage: 100,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : errorMessage;
      setError(message);
    } finally {
      setIsExporting(false);
      setProgress(null);
    }
  };

  return {
    exportData,
    isExporting,
    error,
    progress,
  };
}
